import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { productsApi, userApi } from "@/lib/api";
import ArchetypeCard from "@/components/app/ArchetypeCard";
import { IDS } from "@/constants/testIds";
import { toast } from "sonner";
import { ArrowRight, Lock, Sparkles, RefreshCw } from "lucide-react";

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [unlocks, setUnlocks] = useState(null);
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    productsApi.getMyUnlocks().then(setUnlocks).catch(() => {});
  }, []);

  const arch = user?.archetypes || [];
  const primary = user?.primary_archetypes?.[0] || arch[0];
  const secondary = user?.secondary_archetypes?.[0] || arch[1];
  const hasResults = arch.length > 0;
  const isVerified = user?.is_verified;
  const ownedIds = unlocks?.product_ids || [];
  const hasQuiz = ownedIds.includes("quiz") || isVerified;

  const handleRestore = async () => {
    if (restoring) return;
    setRestoring(true);
    try {
      const res = await userApi.restorePurchases();
      const names = res.granted_product_names || [];
      if (names.length) {
        toast.success(
          names.length === 1
            ? `Added to your Library: ${names[0]}`
            : `${names.length} purchases added to your Library`
        );
        // Pull the latest unlocks so the UI reflects the grant immediately.
        productsApi.getMyUnlocks().then(setUnlocks).catch(() => {});
        await refresh();
      } else {
        toast.message("No new purchases found for this email", {
          description:
            "If you just purchased, please wait a moment and try again. Otherwise, make sure you signed in with the same email you used at checkout.",
        });
      }
    } catch (e) {
      toast.error(e.message || "Could not check for purchases");
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div className="space-y-8" data-testid={IDS.dashboardPage}>
      <header>
        <p className="text-sm mb-1" style={{ color: "var(--pa-text-secondary)" }}>
          Welcome back,
        </p>
        <h1 className="pa-serif text-3xl md:text-4xl" style={{ color: "var(--pa-text-primary)" }}>
          {user?.name || "Investigator"}
        </h1>
      </header>

      {!isVerified && (
        <div className="pa-card p-5 md:p-6">
          <div className="flex items-start gap-3 mb-4">
            <Lock size={18} color="var(--pa-gold)" />
            <div>
              <h2 className="pa-serif text-lg" style={{ color: "var(--pa-text-primary)" }}>Unlock your assessment</h2>
              <p className="text-sm mt-1" style={{ color: "var(--pa-text-secondary)" }}>
                If you already purchased from ParanormalArchetypes.net, tap the button below and we&rsquo;ll link your purchase to this account automatically.
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              className="pa-btn-gold w-full sm:w-auto inline-flex items-center justify-center gap-2"
              onClick={handleRestore}
              disabled={restoring}
              data-testid={IDS.restorePurchasesBtn}
            >
              <RefreshCw size={16} className={restoring ? "animate-spin" : ""} />
              {restoring ? "Checking..." : "I already purchased"}
            </button>
            <button
              className="pa-btn-outline w-full sm:w-auto"
              onClick={() => navigate("/unlock")}
            >
              Enter Access Code
            </button>
          </div>
          <p className="text-xs mt-3" style={{ color: "var(--pa-text-muted)" }}>
            Signed in as <strong>{user?.email}</strong>. Purchases made with a different email won&rsquo;t match &mdash; sign out and sign back in with the checkout email if needed.
          </p>
        </div>
      )}

      {isVerified && !hasResults && (
        <div className="pa-card p-5 md:p-6">
          <h2 className="pa-serif text-lg mb-2" style={{ color: "var(--pa-text-primary)" }}>Take the assessment</h2>
          <p className="text-sm mb-4" style={{ color: "var(--pa-text-secondary)" }}>
            54 questions, about 10 to 15 minutes. Your Primary and Secondary archetypes will be revealed at the end.
          </p>
          <button className="pa-btn-gold" onClick={() => navigate("/quiz")} data-testid={IDS.quizStartBtn}>
            Start Assessment
          </button>
        </div>
      )}

      {hasResults && (
        <section className="space-y-4">
          <h2 className="pa-serif text-xl md:text-2xl" style={{ color: "var(--pa-gold)" }}>Your Archetypes</h2>
          {primary && <ArchetypeCard name={primary} tag="Primary" />}
          {secondary && <ArchetypeCard name={secondary} tag="Secondary" />}
          <div className="flex gap-3 flex-wrap">
            <Link to="/insights" className="pa-btn-outline inline-flex items-center gap-2">
              View Insights <ArrowRight size={16} />
            </Link>
            <Link to="/dynamics" className="pa-btn-outline inline-flex items-center gap-2">
              Dynamics <ArrowRight size={16} />
            </Link>
          </div>
        </section>
      )}

      <section className="space-y-4">
        <h2 className="pa-serif text-xl" style={{ color: "var(--pa-gold)" }}>Quick actions</h2>
        <div className="grid gap-3 md:grid-cols-2">
          <Link to="/library" className="pa-card p-4 md:p-5 flex items-center justify-between hover:border-[color:var(--pa-gold-dark)]" style={{ transition: "border-color 160ms" }}>
            <div>
              <p className="pa-serif text-lg" style={{ color: "var(--pa-text-primary)" }}>Library</p>
              <p className="text-xs mt-1" style={{ color: "var(--pa-text-secondary)" }}>PDFs, Starter Kit, Guidebook, Manual</p>
            </div>
            <ArrowRight size={18} color="var(--pa-gold)" />
          </Link>
          <Link to="/field" className="pa-card p-4 md:p-5 flex items-center justify-between">
            <div>
              <p className="pa-serif text-lg" style={{ color: "var(--pa-text-primary)" }}>Field Mode</p>
              <p className="text-xs mt-1" style={{ color: "var(--pa-text-secondary)" }}>Guidance for before, during, after</p>
            </div>
            <ArrowRight size={18} color="var(--pa-gold)" />
          </Link>
          {hasQuiz && (
            <Link to="/insights" className="pa-card p-4 md:p-5 flex items-center justify-between">
              <div>
                <p className="pa-serif text-lg" style={{ color: "var(--pa-text-primary)" }}>Review Assessment</p>
                <p className="text-xs mt-1" style={{ color: "var(--pa-text-secondary)" }}>Your saved archetype profile</p>
              </div>
              <Sparkles size={18} color="var(--pa-gold)" />
            </Link>
          )}
        </div>
      </section>
    </div>
  );
}
